// @LTG-ID: data/journals/_index (CHG-0102)
// @STATUS: BUILT
// @HERMETIC: true
// @DOCS: /app/docs/data-model/journal_content_v1.md
// @CHANGED: 2026-05-29 session_007 (CHG-0102 · Journal MVP)
// @OWNER: web
//
// Central manifest for the 20 CHG-0102 Journal MVP articles.
//
// Consumed by:
//   - `pages/JournalArticle.jsx` — dynamic /journals/:slug renderer
//   - `scripts/chg_0102_build_sitemap.js` — sitemap.xml generation
//   - future CHG-0104 /journals index redesign (category tabs)
//
// NOT consumed by:
//   - `data/brief-01.js` and `data/brief-02.js` — the two pre-existing
//     hand-JSX briefs remain untouched and continue to route through their
//     dedicated pages (`pages/Brief01.jsx`, `pages/Brief02.jsx`). See
//     `/app/docs/data-model/journal_content_v1.md §3` for the coexistence
//     rationale and the Article-4 slug-collision resolution.

import { BRIEF as howMuchIsMyBusinessWorth } from "./how-much-is-my-business-worth";
import { BRIEF as exitPlanningGuide } from "./exit-planning-guide";
import { BRIEF as whatIsEbitda } from "./what-is-ebitda";
import { BRIEF as sdeVsEbitdaGuide } from "./sde-vs-ebitda-guide";
import { BRIEF as whatIsAValuationMultiple } from "./what-is-a-valuation-multiple";
import { BRIEF as howLongDoesItTakeToSellABusiness } from "./how-long-does-it-take-to-sell-a-business";
import { BRIEF as whatPercentageOfBusinessesSell } from "./what-percentage-of-businesses-sell";
import { BRIEF as ownerDependenceBusinessValue } from "./owner-dependence-business-value";
import { BRIEF as assetSaleVsStockSale } from "./asset-sale-vs-stock-sale";
import { BRIEF as silverTsunamiBusinessStatistics } from "./silver-tsunami-business-statistics";
import { BRIEF as businessValueDrivers } from "./business-value-drivers";
import { BRIEF as recurringRevenueBusinessValue } from "./recurring-revenue-business-value";
import { BRIEF as customerConcentrationSellingBusiness } from "./customer-concentration-selling-business";
import { BRIEF as cleanFinancialRecordsBusinessValue } from "./clean-financial-records-business-value";
import { BRIEF as managementTeamBusinessValue } from "./management-team-business-value";
import { BRIEF as businessSystemsProcessesValue } from "./business-systems-processes-value";
import { BRIEF as doesGrowthIncreaseBusinessValue } from "./does-growth-increase-business-value";
import { BRIEF as addBacksSellingABusiness } from "./add-backs-selling-a-business";
import { BRIEF as dueDiligenceSellingABusiness } from "./due-diligence-selling-a-business";
import { BRIEF as increaseBusinessValueBeforeSelling } from "./increase-business-value-before-selling";

// CHG-0108 · New 20 articles (Batch 3 + Batch 4, articles 21-40).
import { BRIEF as personalReadinessToExit } from "./personal-readiness-to-exit";
import { BRIEF as walkAwayNumberSellingBusiness } from "./walk-away-number-selling-business";
import { BRIEF as lifeAfterSellingBusiness } from "./life-after-selling-business";
import { BRIEF as sellerFinancingBusinessSale } from "./seller-financing-business-sale";
import { BRIEF as earnoutBusinessSale } from "./earnout-business-sale";
import { BRIEF as sbaFinancingBusinessSale } from "./sba-financing-business-sale";
import { BRIEF as familySuccessionVsSelling } from "./family-succession-vs-selling";
import { BRIEF as sellBusinessToEmployees } from "./sell-business-to-employees";
import { BRIEF as sellBusinessConfidentially } from "./sell-business-confidentially";
import { BRIEF as doINeedABusinessBroker } from "./do-i-need-a-business-broker";
import { BRIEF as sellingHvacPlumbingElectricalBusiness } from "./selling-hvac-plumbing-electrical-business";
import { BRIEF as sellingADentalPractice } from "./selling-a-dental-practice";
import { BRIEF as sellingAMedicalPractice } from "./selling-a-medical-practice";
import { BRIEF as sellingARestaurant } from "./selling-a-restaurant";
import { BRIEF as sellingARetailBusiness } from "./selling-a-retail-business";
import { BRIEF as sellingAManufacturingBusiness } from "./selling-a-manufacturing-business";
import { BRIEF as sellingAnAutoRepairShop } from "./selling-an-auto-repair-shop";
import { BRIEF as sellingAProfessionalServicesFirm } from "./selling-a-professional-services-firm";
import { BRIEF as sellingAConstructionCompany } from "./selling-a-construction-company";
import { BRIEF as whereToStartBusinessValueExit } from "./where-to-start-business-value-exit";

// CHG-0104 · Import the two legacy hand-JSX briefs so the /journals index
// page can render all 22 published articles from a single source of truth.
// The legacy files export only FRONTMATTER (no BRIEF wrapper, no
// articleSection, no ogImage). We adapt them here without touching the
// source files — brief-01.js + brief-02.js remain untouched per the
// standing CHG-0102 §"Untouched" contract.
import { BRIEF_01_SLUG, BRIEF_01_FRONTMATTER } from "../brief-01";
import { BRIEF_02_SLUG, BRIEF_02_FRONTMATTER } from "../brief-02";

export const JOURNAL_ARTICLES = {
    "how-much-is-my-business-worth": howMuchIsMyBusinessWorth,
    "exit-planning-guide": exitPlanningGuide,
    "what-is-ebitda": whatIsEbitda,
    "sde-vs-ebitda-guide": sdeVsEbitdaGuide,
    "what-is-a-valuation-multiple": whatIsAValuationMultiple,
    "how-long-does-it-take-to-sell-a-business": howLongDoesItTakeToSellABusiness,
    "what-percentage-of-businesses-sell": whatPercentageOfBusinessesSell,
    "owner-dependence-business-value": ownerDependenceBusinessValue,
    "asset-sale-vs-stock-sale": assetSaleVsStockSale,
    "silver-tsunami-business-statistics": silverTsunamiBusinessStatistics,
    "business-value-drivers": businessValueDrivers,
    "recurring-revenue-business-value": recurringRevenueBusinessValue,
    "customer-concentration-selling-business": customerConcentrationSellingBusiness,
    "clean-financial-records-business-value": cleanFinancialRecordsBusinessValue,
    "management-team-business-value": managementTeamBusinessValue,
    "business-systems-processes-value": businessSystemsProcessesValue,
    "does-growth-increase-business-value": doesGrowthIncreaseBusinessValue,
    "add-backs-selling-a-business": addBacksSellingABusiness,
    "due-diligence-selling-a-business": dueDiligenceSellingABusiness,
    "increase-business-value-before-selling": increaseBusinessValueBeforeSelling,
    // CHG-0108 · New 20 articles (21-40).
    "personal-readiness-to-exit": personalReadinessToExit,
    "walk-away-number-selling-business": walkAwayNumberSellingBusiness,
    "life-after-selling-business": lifeAfterSellingBusiness,
    "seller-financing-business-sale": sellerFinancingBusinessSale,
    "earnout-business-sale": earnoutBusinessSale,
    "sba-financing-business-sale": sbaFinancingBusinessSale,
    "family-succession-vs-selling": familySuccessionVsSelling,
    "sell-business-to-employees": sellBusinessToEmployees,
    "sell-business-confidentially": sellBusinessConfidentially,
    "do-i-need-a-business-broker": doINeedABusinessBroker,
    "selling-hvac-plumbing-electrical-business": sellingHvacPlumbingElectricalBusiness,
    "selling-a-dental-practice": sellingADentalPractice,
    "selling-a-medical-practice": sellingAMedicalPractice,
    "selling-a-restaurant": sellingARestaurant,
    "selling-a-retail-business": sellingARetailBusiness,
    "selling-a-manufacturing-business": sellingAManufacturingBusiness,
    "selling-an-auto-repair-shop": sellingAnAutoRepairShop,
    "selling-a-professional-services-firm": sellingAProfessionalServicesFirm,
    "selling-a-construction-company": sellingAConstructionCompany,
    "where-to-start-business-value-exit": whereToStartBusinessValueExit,
};

export const JOURNAL_ARTICLES_LIST = Object.values(JOURNAL_ARTICLES);

export const JOURNAL_ARTICLE_SLUGS = Object.keys(JOURNAL_ARTICLES);

// Convenience: slugs of the two legacy hand-JSX briefs (untouched by CHG-0102),
// consumed by the sitemap generator so its URL list covers the full 22 routes.
export const LEGACY_BRIEF_SLUGS = [
    "what-is-my-business-worth",
    "sde-vs-ebitda",
];

// -----------------------------------------------------------------------
// CHG-0104 · Legacy-brief adapter shims + unified 22-article list.
//
// The legacy hand-JSX briefs (brief-01.js + brief-02.js) shipped a
// slimmer FRONTMATTER shape than the CHG-0102 BRIEF spec. The new
// /journals magazine index needs a single 22-article list with a
// consistent shape, so we adapt the legacy briefs here — the source
// files are NOT touched (see CHG-0102 §"Untouched" contract).
//
// Adapter output must match the BRIEF-shape fields consumed by
// `pages/Journals.jsx` and `scripts/chg_0102_inject_meta.js` when
// generating the /journals Blog JSON-LD.
// -----------------------------------------------------------------------

const LEGACY_SECTION_TO_ARTICLE_SECTION = {
    "Core Valuation": "Valuation",
};

function adaptLegacyBrief(slug, legacyFm) {
    const articleSection =
        LEGACY_SECTION_TO_ARTICLE_SECTION[legacyFm.section] || legacyFm.section;
    return {
        SLUG: slug,
        FRONTMATTER: {
            number: legacyFm.number,
            section: articleSection, // Normalize display label to match tab taxonomy.
            sectionHref: `/journals#${legacyFm.sectionAnchor || "core-valuation"}`,
            sectionAnchor: legacyFm.sectionAnchor || "core-valuation",
            title: legacyFm.title,
            deck: legacyFm.deck,
            author: legacyFm.author,
            authorRole: legacyFm.authorRole,
            authorInitials: legacyFm.authorInitials,
            publishedISO: legacyFm.publishedISO,
            publishedHuman: legacyFm.publishedHuman,
            lastReviewed: legacyFm.publishedISO,
            readMinutes: legacyFm.readMinutes,
            wordCount: null, // Not tracked in legacy shape.
            timeRequired: `PT${legacyFm.readMinutes}M`,
            keywords: "",
            articleSection,
            articleBodyPreview: legacyFm.deck || "",
            complianceNote: "",
            needsDisclaimerFooter: false,
            internalLinks: [],
            ctaId: legacyFm.ctaId,
            ctaLabel: legacyFm.ctaLabel,
            pageTitle: legacyFm.pageTitle,
            canonical: legacyFm.canonical,
            ogImage: `/journals/og/${slug}.jpg`,
        },
        // Legacy briefs don't ship the CHG-0102 markdown/FAQ/END_CTA/READ_NEXT
        // payloads — those are hand-JSX inside pages/Brief0{1,2}.jsx.
        // The magazine index only reads SLUG + FRONTMATTER, so the
        // remaining fields intentionally stay undefined here.
    };
}

const LEGACY_BRIEF_01 = adaptLegacyBrief(BRIEF_01_SLUG, BRIEF_01_FRONTMATTER);
const LEGACY_BRIEF_02 = adaptLegacyBrief(BRIEF_02_SLUG, BRIEF_02_FRONTMATTER);

// The unified 22-article list consumed by the CHG-0104 /journals magazine
// index. Order does not matter — the index page sorts by publishedISO desc.
export const ALL_PUBLISHED_ARTICLES = [
    LEGACY_BRIEF_01,
    LEGACY_BRIEF_02,
    ...JOURNAL_ARTICLES_LIST,
];

export default JOURNAL_ARTICLES;
